/****************
 * CELEBRATIONS: CONFETTI AND A CHIME. 2026-10-21, engagement redesign.
 *
 * Guillaume, 2026-10-21: "Rewards, sounds, confetti, celebrations and
 * streak-style progress are WELCOME. CopyFi is not a casino: the house does
 * not win, and CopyFi earns when its users win and come back."
 *
 * Twin file: frontend/src/lib/celebrate.core.ts and
 * mobile-app/src/lib/celebrate.core.ts must be byte-identical (the phone's /m
 * is a web build, so the same DOM code runs there). No packages: a canvas for
 * the confetti, WebAudio for the chime, so nothing is downloaded.
 *
 * Sound is on by default with a mute (Settings and the toast itself); the
 * choice is this browser's (localStorage), as a volume is.
 *
 * What is celebrated: a winning close, a milestone, the first copied trade.
 * Never a near-miss ("almost hit TP") and never anything after a loss.
 ****************/

export type CelebrationSize = 'small' | 'big';

const SOUND_KEY = 'copyfi.sound.wins';

export function soundOn(): boolean {
  try {
    return globalThis.localStorage?.getItem(SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setSoundOn(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(SOUND_KEY, on ? 'on' : 'off');
  } catch {
    /* private mode: the choice lasts this visit only */
  }
}

function reducedMotion(): boolean {
  try {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

let audio: AudioContext | null = null;

/** A short rising chime: two notes for small, three for big. */
export function chime(size: CelebrationSize = 'small'): void {
  if (!soundOn()) return;
  try {
    const Ctx = (globalThis as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
      .AudioContext ?? (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    audio = audio ?? new Ctx();
    const ctx = audio;
    if (ctx.state === 'suspended') void ctx.resume();
    const notes = size === 'big' ? [659.25, 783.99, 1046.5] : [783.99, 1046.5];
    notes.forEach((f, i) => {
      const t = ctx.currentTime + i * 0.11;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.5);
    });
  } catch {
    /* no audio on this device: the confetti still shows */
  }
}

/**
 * "Cha-ching": money landing, for a winning trade (Guillaume, 2026-10-21: "a
 * specific satisfying catching money sound when someone wins a trade"). Two
 * bright coin strikes, the second higher, over a short metallic rattle, all
 * made here (no sound file to download).
 */
export function chaChing(): void {
  if (!soundOn()) return;
  try {
    const Ctx = (globalThis as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
      .AudioContext ?? (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    audio = audio ?? new Ctx();
    const ctx = audio;
    if (ctx.state === 'suspended') void ctx.resume();
    const now = ctx.currentTime;
    // The rattle of coins: a short burst of filtered noise.
    const len = Math.floor(ctx.sampleRate * 0.35);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 6000;
    band.Q.value = 1.2;
    const ng = ctx.createGain();
    ng.gain.value = 0.12;
    noise.connect(band).connect(ng).connect(ctx.destination);
    noise.start(now + 0.02);
    // "Cha" then "ching": two struck metal tones, each with a bright partial.
    const strike = (t: number, f: number, peak: number, decay: number) => {
      for (const [mult, amp] of [[1, 1], [2.76, 0.45], [5.4, 0.25]] as const) {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = f * mult;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(peak * amp, t + 0.005);
        g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
        osc.connect(g).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + decay + 0.05);
      }
    };
    strike(now, 1568, 0.16, 0.18);
    strike(now + 0.11, 2093, 0.2, 0.9);
  } catch {
    /* no audio on this device: the confetti still shows */
  }
}

/** A winning trade: big confetti and the cha-ching. */
export function celebrateWin(): void {
  confetti('big');
  chaChing();
}

const COLORS = ['#3b82f6', '#6366f1', '#22c55e', '#facc15', '#f472b6', '#38bdf8'];

/** Confetti over the whole window, gone after ~2.5 s. */
export function confetti(size: CelebrationSize = 'small'): void {
  if (typeof document === 'undefined' || reducedMotion()) return;
  const canvas = document.createElement('canvas');
  const dpr = Math.min(globalThis.devicePixelRatio || 1, 2);
  const w = globalThis.innerWidth;
  const h = globalThis.innerHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  Object.assign(canvas.style, {
    position: 'fixed', inset: '0', width: `${w}px`, height: `${h}px`,
    pointerEvents: 'none', zIndex: '2147483646',
  });
  canvas.setAttribute('aria-hidden', 'true');
  document.body.appendChild(canvas);
  const g = canvas.getContext('2d');
  if (!g) {
    canvas.remove();
    return;
  }
  g.scale(dpr, dpr);
  const n = size === 'big' ? 160 : 70;
  const parts = Array.from({ length: n }, (_, i) => {
    const fromLeft = i % 2 === 0;
    const angle = (fromLeft ? -60 : -120) * (Math.PI / 180) + (Math.random() - 0.5) * 0.9;
    const speed = (size === 'big' ? 13 : 10) * (0.6 + Math.random() * 0.6);
    return {
      x: fromLeft ? w * 0.15 : w * 0.85, y: h * 0.75,
      vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
      r: 4 + Math.random() * 4, spin: Math.random() * Math.PI, vs: (Math.random() - 0.5) * 0.3,
      c: COLORS[i % COLORS.length],
    };
  });
  const start = performance.now();
  const step = (now: number) => {
    const t = now - start;
    g.clearRect(0, 0, w, h);
    for (const p of parts) {
      p.vy += 0.32;
      p.vx *= 0.985;
      p.x += p.vx;
      p.y += p.vy;
      p.spin += p.vs;
      g.save();
      g.globalAlpha = Math.max(0, 1 - t / 2500);
      g.translate(p.x, p.y);
      g.rotate(p.spin);
      g.fillStyle = p.c;
      g.fillRect(-p.r, -p.r / 2, p.r * 2, p.r);
      g.restore();
    }
    if (t < 2500) requestAnimationFrame(step);
    else canvas.remove();
  };
  requestAnimationFrame(step);
}

/** Confetti + chime together. The screen shows its own message beside it. */
export function celebrate(size: CelebrationSize = 'small'): void {
  confetti(size);
  chime(size);
}
